// Analytics OAuth Callback Handler
// LazySEM Frontend - Handles OAuth callback from Google

import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { completeOAuth } from "../../api/analytics";
import styles from "./AnalyticsOAuthCallback.module.css";

import { logger } from "../../utils/logger";
import { useOrganization } from "../../contexts/OrganizationContext";
import { takeAnalyticsReturn } from "../../features/analytics/oauthReturn";

export default function AnalyticsOAuthCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { currentOrganization, loading: organizationLoading } =
    useOrganization();
  const [status, setStatus] = useState<"loading" | "success" | "error">(
    "loading",
  );
  const [message, setMessage] = useState("Completing Google connection...");

  // Prevent duplicate API calls in React Strict Mode (dev runs effects twice)
  const hasProcessedCallback = useRef(false);

  useEffect(() => {
    const completeConnection = async () => {
      if (organizationLoading || !user) return;
      // Guard against duplicate calls — Google auth codes are single-use
      if (hasProcessedCallback.current) {
        logger.log(
          "🛑 Analytics OAuth callback already processed, skipping duplicate",
        );
        return;
      }
      hasProcessedCallback.current = true;

      try {
        // Get code and state from URL parameters
        const code = searchParams.get("code");
        const state = searchParams.get("state");

        if (!code || !state) {
          setStatus("error");
          setMessage(
            "Missing authorization code or state. Please try connecting again.",
          );
          return;
        }

        // Call the backend to complete OAuth
        const response = await completeOAuth({ code, state });

        if (response.success) {
          setStatus("success");
          setMessage(
            `Successfully connected to Google as ${response.google_email}`,
          );

          const destination = takeAnalyticsReturn(
            user.id,
            state,
            currentOrganization?.id ?? null,
          );
          setTimeout(() => {
            navigate(destination, { replace: true });
          }, 2000);
        } else {
          setStatus("error");
          setMessage(response.message || "Failed to complete OAuth connection");
        }
      } catch (error: unknown) {
        logger.error("OAuth completion error:", error);
        setStatus("error");
        setMessage(
          (error instanceof Error ? error.message : null) ||
            "An error occurred while connecting to Google. Please try again.",
        );
      }
    };

    completeConnection();
  }, [
    searchParams,
    user,
    navigate,
    organizationLoading,
    currentOrganization?.id,
  ]);

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        {status === "loading" && (
          <>
            <div className={styles.spinner}></div>
            <h2 className={styles.title}>Connecting to Google...</h2>
            <p className={styles.message}>{message}</p>
          </>
        )}

        {status === "success" && (
          <>
            <div className={styles.successIcon}>✓</div>
            <h2 className={styles.title}>Connection Successful!</h2>
            <p className={styles.message}>{message}</p>
            <p className={styles.redirect}>
              Redirecting to Analytics dashboard...
            </p>
          </>
        )}

        {status === "error" && (
          <>
            <div className={styles.errorIcon}>✕</div>
            <h2 className={styles.title}>Connection Failed</h2>
            <p className={styles.message}>{message}</p>
            <button
              className={styles.retryButton}
              onClick={() => navigate("/analytics", { replace: true })}
            >
              Return to Analytics
            </button>
          </>
        )}
      </div>
    </div>
  );
}
